/**
 * SummaryTab Component
 * Final totals, markup, and PDF export
 */

import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Card, Button, EditableCell } from '../ui/FormElements';
import { formatCurrency } from '../../utils/calculations';

const SummaryTab = ({ materialsTotal, labourTotal, summary, onChange, totalArea, projectId }) => {
  const [exporting, setExporting] = useState(false);
  const { getToken } = useAuth();
  
  const updateSummary = (field, value) => {
    onChange({ ...summary, [field]: value });
  };

  // Calculations
  const subtotal1 = materialsTotal + labourTotal;
  const overhead = subtotal1 * (summary.overheadPercent || 0) / 100;
  const subtotal2 = subtotal1 + overhead;
  const profit = subtotal2 * (summary.profitPercent || 0) / 100;
  const totalExVat = subtotal2 + profit;
  const vat = totalExVat * (summary.vatPercent || 20) / 100;
  const totalIncVat = totalExVat + vat;
  const ratePerM2 = totalArea > 0 ? totalExVat / totalArea : 0;

  const handleExportPDF = async () => {
    if (!projectId) return;
    
    try {
      setExporting(true);
      const token = await getToken();
      const API_URL = import.meta.env.VITE_API_URL || '';
      
      const response = await fetch(`${API_URL}/pdf/estimate/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate PDF');
      }
      
      // Get the blob and create download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `estimate-${projectId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Materials & Labour */}
      <Card title="Materials & Labour">
        <div className="space-y-4">
          <div className="flex justify-between py-2 border-b border-slate-700">
            <span className="text-slate-400">Materials</span>
            <span className="text-white font-medium">{formatCurrency(materialsTotal)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-slate-700">
            <span className="text-slate-400">Labour</span>
            <span className="text-white font-medium">{formatCurrency(labourTotal)}</span>
          </div>
          <div className="flex justify-between py-2 bg-slate-700/30 px-2 -mx-2">
            <span className="text-white font-semibold">Subtotal</span>
            <span className="text-white font-semibold">{formatCurrency(subtotal1)}</span>
          </div>
        </div>
      </Card>

      {/* Adjustments & Totals */}
      <Card title="Adjustments & Totals">
        <div className="space-y-4">
          {/* Overhead */}
          <div className="flex justify-between items-center py-2 border-b border-slate-700">
            <span className="text-slate-400">Overhead</span>
            <div className="flex items-center gap-2">
              <span className="text-slate-400">{formatCurrency(overhead)}</span>
              <div className="w-20 bg-amber-500/10 rounded">
                <EditableCell
                  value={summary.overheadPercent || 0}
                  onChange={(v) => updateSummary('overheadPercent', v)}
                  suffix="%"
                  highlight
                />
              </div>
            </div>
          </div>
          
          {/* Subtotal 2 */}
          <div className="flex justify-between py-2 border-b border-slate-700">
            <span className="text-slate-400">Subtotal 2</span>
            <span className="text-white">{formatCurrency(subtotal2)}</span>
          </div>
          
          {/* Profit */}
          <div className="flex justify-between items-center py-2 border-b border-slate-700">
            <span className="text-slate-400">Profit</span>
            <div className="flex items-center gap-2">
              <span className="text-slate-400">{formatCurrency(profit)}</span>
              <div className="w-20 bg-amber-500/10 rounded">
                <EditableCell
                  value={summary.profitPercent || 0}
                  onChange={(v) => updateSummary('profitPercent', v)}
                  suffix="%"
                  highlight
                />
              </div>
            </div>
          </div>
          
          {/* Total ex VAT */}
          <div className="flex justify-between py-3 bg-amber-500/20 px-2 -mx-2 rounded">
            <span className="text-amber-500 font-bold text-lg">Total (ex VAT)</span>
            <span className="text-amber-500 font-bold text-lg">{formatCurrency(totalExVat)}</span>
          </div>
          
          {/* VAT */}
          <div className="flex justify-between items-center py-2 border-b border-slate-700">
            <span className="text-slate-400">VAT</span>
            <div className="flex items-center gap-2">
              <span className="text-slate-400">{formatCurrency(vat)}</span>
              <div className="w-20 bg-amber-500/10 rounded">
                <EditableCell
                  value={summary.vatPercent || 20}
                  onChange={(v) => updateSummary('vatPercent', v)}
                  suffix="%"
                  highlight
                />
              </div>
            </div>
          </div>
          
          {/* Total inc VAT */}
          <div className="flex justify-between py-3 bg-slate-700/50 px-2 -mx-2 rounded">
            <span className="text-white font-bold text-lg">Total (inc VAT)</span>
            <span className="text-white font-bold text-lg">{formatCurrency(totalIncVat)}</span>
          </div>
        </div>
      </Card>

      {/* Breakdown */}
      <Card title="Breakdown">
        <div className="space-y-3">
          <div className="flex justify-between py-2">
            <span className="text-slate-400">Total Area</span>
            <span className="text-white">{totalArea.toFixed(2)} m²</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-400">Rate per m²</span>
            <span className="text-amber-500 font-semibold">{formatCurrency(ratePerM2)}</span>
          </div>
          <div className="flex justify-between py-2">
            <span className="text-slate-400">Profit %</span>
            <span className="text-white">{((profit / subtotal2) * 100 || 0).toFixed(1)}%</span>
          </div>
        </div>
      </Card>

      {/* Export */}
      <Card title="Export">
        <div className="space-y-4">
          <p className="text-slate-400 text-sm">
            Download your estimate as a professional PDF document.
          </p>
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={handleExportPDF} 
              disabled={exporting || !projectId}
            >
              {exporting ? (
                <>
                  <span className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full mr-2"></span>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export PDF
                </>
              )}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default SummaryTab;
